import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectFilteredShipments,
  selectShipmentFilterVisible,
  selectShipmentLoading,
  selectShipmentLoadingMore,
  selectShipmentHasMore,
  selectfetchShipmentLoading,
} from './store/selectors';
import VirtualList from '../components/VirtualList'; // Import VirtualList
import LoadingSpinner from '../../InitialPage/Sidebar/LoadingSpinner'; // Import LoadingSpinner
import { isHeaderVisible } from 'Core/redux/main/selectors';
import { MODAL_TYPES } from 'Feature/components/app-modals';
import { showModal } from 'Core/redux/main/slice';
import { Edit, Trash2 } from 'react-feather';
import { Button, ButtonGroup } from 'react-bootstrap';
import { deleteShipment, editShipmentData, loadMoreShipments } from './store/slice';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import { all_routes } from '../../router/all_routes';
import { format } from 'date-fns';

const { SHIPMENT_ADD_EDIT } = MODAL_TYPES;

const ShipmentTile = props => {
  const dispatch = useDispatch();
  const [columnCount, setColumnCount] = useState(6);
  const [columnWidth, setColumnWidth] = useState(300);
  const shipmentTileRef = useRef(null);

  const filteredShipments = useSelector(selectFilteredShipments);
  const isLoading = useSelector(selectShipmentLoading);
  const isLoadingMore = useSelector(selectShipmentLoadingMore);
  const hasMore = useSelector(selectShipmentHasMore);
  const filterVisible = useSelector(selectShipmentFilterVisible);
  const isFetchDataLoading = useSelector(selectfetchShipmentLoading);
  const headerVisible = useSelector(isHeaderVisible);

  const updateColumnSettings = useCallback(() => {
    if (shipmentTileRef.current) {
      const width = shipmentTileRef.current.offsetWidth;
      let columns = 6;

      if (width < 576) {
        columns = 1; // Mobile
      } else if (width < 768) {
        columns = 3; // Tablets
      } else if (width < 992) {
        columns = 5; // Large screens
      } else {
        columns = 6; // Extra large screens
      }

      setColumnCount(columns);
      setColumnWidth(width / columns);
    }
  }, []);

  useEffect(() => {
    updateColumnSettings();
    window.addEventListener('resize', updateColumnSettings);
    return () => window.removeEventListener('resize', updateColumnSettings);
  }, [updateColumnSettings]);

  const itemData = (filteredShipments || []).map(shipment => <Tile shipment={shipment} />);
  const rowCount = Math.ceil((filteredShipments || []).length / columnCount) || 0;

  const handleRowsRendered = useCallback(
    visibleRowStopIndex => {
      if (!hasMore || isLoading || isLoadingMore || rowCount === 0) {
        return;
      }
      // Load the next page when the user scrolls near the last visible rows.
      if (visibleRowStopIndex >= rowCount - 2) {
        dispatch(loadMoreShipments());
      }
    },
    [dispatch, hasMore, isLoading, isLoadingMore, rowCount],
  );

  return (
    <div
      className={`shipment-tile-body ${filterVisible ? 'filter-visible' : ''} ${!headerVisible ? 'header-collapsed' : ''
        }`}
      ref={shipmentTileRef}
      style={{ position: 'relative' }}
    >
      {(isLoading || isFetchDataLoading) && <LoadingSpinner />}
      {
        <VirtualList
          columnCount={columnCount}
          rowCount={rowCount}
          itemData={itemData}
          columnWidth={columnWidth}
          rowHeight={150} // Adjusted row height
          onRowsRendered={handleRowsRendered}
        />
      }
      {isLoadingMore && (
        <div
          className="shipment-load-more-loader"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            padding: '14px 16px',
            background: 'rgba(255, 255, 255, 0.95)',
            borderTop: '1px solid #dbe0e6',
            boxShadow: '0 -6px 16px rgba(0, 0, 0, 0.08)',
          }}
        >
          <div
            className="spinner-border spinner-border-sm text-primary"
            role="status"
            aria-hidden="true"
          />
          <span style={{ fontSize: 13, color: '#475467', fontWeight: 500 }}>
            Loading more shipments...
          </span>
        </div>
      )}
    </div>
  );
};
const MySwal = withReactContent(Swal);

const Tile = ({ shipment }) => {
  const dispatch = useDispatch();

  const handleEdit = e => {
    e.stopPropagation();
    dispatch(editShipmentData(shipment.id));
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      dispatch(deleteShipment(shipment.id));
    }
  };
  const isShipments = window.location.pathname == all_routes.shipments;

  return (
    <div key={shipment.id || Math.random()} className="shipment-tile">
      <div
        className={`card p-3 m-2 ${shipment.isNewlyCreated ? 'border-success' : ''}`}
        style={{
          border: '1px solid #ddd',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div className="tile-actions">
          <ButtonGroup size="sm">
            <Button variant="light" onClick={handleEdit}>
              <Edit size={16} />
            </Button>
            {!isShipments && (
              <Button variant="light" onClick={handleDelete}>
                <Trash2 size={16} />
              </Button>
            )}
          </ButtonGroup>
        </div>
        {shipment.customerShortName && (
          <div className="mb-2" style={{ marginTop: '0', paddingTop: '0' }}>
            <h6 className="mb-0 fw-bold" style={{ fontSize: '1rem', fontWeight: '600', lineHeight: '1.2' }}>{shipment.customerShortName}</h6>
          </div>
        )}
        <div className="d-flex justify-content-between">
          <div className="d-flex flex-row align-items-center">
            <div className="ms-2 c-details">
              <h6 className="mb-0">{shipment.asp} </h6>
              {/* <span>{shipment.shipDate}</span> */}

            </div>
          </div>
        </div>

        <div className="mt-2 d-flex flex-wrap gap-2">
          <span
            className="badge"
            title={shipment.status || 'N/A'}
            style={{
              backgroundColor: shipment.backgroundColor || '#e0e0e0',
              color: shipment.textColor || '#000000',
            }}
          >
            {shipment.status === 'Loaded and Ready to Be Picked'
              ? 'Loaded and Ready'
              : shipment.status || 'N/A'}
          </span>
          <span className="badge"
            style={{
              backgroundColor: 'transparent',
              color: '#000000',
            }}
          >
            {shipment.shipDate ? format(new Date(shipment.shipDate), 'MM/dd/yyyy') : 'N/A'}
          </span>

        </div>

        <div className="mt-2 d-flex flex-wrap gap-2">
          <span
            className="badge"
            style={{
              backgroundColor: 'transparent',
              color: '#f0b429',
            }}
          >
            PSN: {shipment.packingSlipNumber || 'N/A'}
          </span>
          <span
            className="badge"
            style={{
              backgroundColor: 'transparent',
              color: '#5bc0de',
            }}
          >
            TR: {shipment.trailer || 'N/A'}
          </span>

        </div>

        <div className="mt-2 d-flex flex-wrap gap-2">
          <span
            className="badge"
            style={{
              backgroundColor: 'transparent',
              color: '#f0b429',
            }}
          >
            {shipment.customerName}
          </span>
          <span
            className="badge"
            style={{
              backgroundColor: 'transparent',
              color: '#5bc0de',
            }}
          >
            {shipment.carrier}
          </span>

        </div>

      </div>
    </div>
  );
};

export default ShipmentTile;
